import { deleteMember, createMember, listMembers } from '../api/members';
import { previewVoicePack } from '../api/tts';
import { userMessage } from '../api/client';
import { PERSON_NAME_RE } from '../lib/group';
import { el, showToast } from '../ui/dom';

const ADULT_VOICES: { id: string; label: string; emoji: string; preview?: boolean }[] = [
  { id: 'random', label: 'Surprise me', emoji: '🎲' },
  { id: 'warm_mom', label: 'Warm Mom', emoji: '💗', preview: true },
  { id: 'calm_dad', label: 'Calm Dad', emoji: '🧔', preview: true },
  { id: 'cheerful_friend', label: 'Cheerful Friend', emoji: '🌻', preview: true },
];

const CHILD_VOICES: { id: string; label: string; emoji: string; preview?: boolean }[] = [
  { id: 'random', label: 'Surprise me', emoji: '🎲' },
  { id: 'holiday', label: 'Holiday Grandpa', emoji: '🎅', preview: true },
  { id: 'dino', label: 'Friendly Dino', emoji: '🦖', preview: true },
  { id: 'sparkle', label: 'Sparkle Fairy', emoji: '🧚', preview: true },
  { id: 'robot', label: 'Robot Pal', emoji: '🤖', preview: true },
  { id: 'captain', label: 'Adventure Captain', emoji: '🏴‍☠️', preview: true },
];

const VOICE_LABEL: Record<string, string> = Object.fromEntries(
  [...ADULT_VOICES, ...CHILD_VOICES].map((v) => [v.id, v.label]),
);

const VOICE_EMOJI: Record<string, string> = Object.fromEntries(
  [...ADULT_VOICES, ...CHILD_VOICES].map((v) => [v.id, v.emoji]),
);

let previewAudio: HTMLAudioElement | null = null;
let previewBusy = false;

async function previewVoice(evt: Event, voiceId: string, playBtn: HTMLButtonElement): Promise<void> {
  evt.preventDefault();
  evt.stopPropagation();
  if (previewBusy) return;

  const label = VOICE_LABEL[voiceId] ?? voiceId;
  if (previewAudio) {
    previewAudio.pause();
    previewAudio = null;
  }

  previewBusy = true;
  const prev = playBtn.textContent;
  playBtn.disabled = true;
  playBtn.classList.add('playing');
  playBtn.textContent = '…';
  showToast(`▶ ${label}`);

  try {
    const res = await previewVoicePack(voiceId);
    const url = res.tts?.audio_url;
    if (!url) {
      showToast(res.tts?.error === 'tts_failed' ? 'Preview unavailable right now' : 'Preview failed');
      return;
    }
    const audio = new Audio(url);
    previewAudio = audio;
    await audio.play();
  } catch (e) {
    showToast(userMessage(e));
  } finally {
    previewBusy = false;
    playBtn.disabled = false;
    playBtn.classList.remove('playing');
    playBtn.textContent = prev || '▶';
  }
}

export async function renderFamily(container: HTMLElement): Promise<void> {
  container.innerHTML = '<p class="list-empty">Loading…</p>';

  try {
    const members = await listMembers();
    container.innerHTML = '';

    const card = el('div', { className: 'card' });
    card.append(
      el('div', { className: 'card-title' }, 'Names and voices'),
      el(
        'div',
        { className: 'card-note' },
        'Add each person’s name so Remindo can write it correctly when you assign tasks by voice.',
      ),
    );

    const chips = el('div', { className: 'member-chips', id: 'memberList' });
    for (const m of members) {
      const chip = el('span', { className: 'member-chip' });
      const pack = m.voice_pack || 'warm_mom';
      const emoji = VOICE_EMOJI[pack] ?? '';
      const voice = VOICE_LABEL[pack] ?? pack;
      chip.append(
        el('span', { className: 'mc-name' }, m.name),
        el('span', { className: 'mc-voice' }, emoji ? `${emoji} ${voice}` : voice),
      );
      const x = el('button', { className: 'chip-x mc-del', type: 'button' }, '×') as HTMLButtonElement;
      x.setAttribute('aria-label', 'Remove');
      x.onclick = async () => {
        if (!confirm(`Remove ${m.name} and all their data? This can't be undone.`)) return;
        try {
          await deleteMember(m.member_id);
          await renderFamily(container);
          showToast('Removed');
        } catch (e) {
          showToast(userMessage(e));
        }
      };
      chip.append(x);
      chips.append(chip);
    }

    const addChip = el('button', { className: 'add-member-chip', type: 'button' }, '+ Add') as HTMLButtonElement;
    chips.append(addChip);
    card.append(chips);

    const form = el('div', { className: 'add-form' });
    form.style.display = 'none';
    form.style.marginTop = '12px';
    const nameField = el('div', { className: 'field' });
    nameField.append(el('label', {}, 'Name'));
    const nameInput = el('input', {
      type: 'text',
      placeholder: 'Like "Grandma" or "Emma"',
      maxlength: '12',
    }) as HTMLInputElement;
    nameField.append(nameInput);

    const roleField = el('div', { className: 'field' });
    roleField.append(el('label', {}, 'Age group'));
    const roleSelect = el('select') as HTMLSelectElement;
    roleSelect.append(
      new Option('Adult', 'adult'),
      new Option('Child', 'child'),
    );
    roleField.append(roleSelect);

    const voiceField = el('div', { className: 'field' });
    voiceField.append(el('label', {}, 'Pick a voice'));
    const voicePick = el('div', { className: 'voice-pick' });

    const renderVoices = () => {
      voicePick.innerHTML = '';
      const role = roleSelect.value === 'child' ? 'child' : 'adult';
      const list = role === 'child' ? CHILD_VOICES : ADULT_VOICES;
      const preferred = role === 'child' ? 'sparkle' : 'warm_mom';
      for (const v of list) {
        const row = el('div', { className: 'voice-row' });
        const id = `vp-${role}-${v.id}`;
        const radio = el('input', {
          type: 'radio',
          name: 'voicePack',
          value: v.id,
          id,
        }) as HTMLInputElement;
        radio.checked = v.id === preferred;
        const label = el('label', { className: 'voice-label' }) as HTMLLabelElement;
        label.htmlFor = id;
        label.append(
          el('span', { className: 'vl-emoji' }, v.emoji),
          el('span', { className: 'vl-name' }, v.label),
        );
        row.append(radio, label);
        if (v.preview) {
          const play = el('button', { className: 'vl-play', type: 'button' }, '▶') as HTMLButtonElement;
          play.setAttribute('aria-label', 'Preview');
          play.onclick = (e) => {
            void previewVoice(e, v.id, play);
          };
          row.append(play);
        }
        row.onclick = (e) => {
          if ((e.target as HTMLElement).closest('.vl-play')) return;
          radio.checked = true;
        };
        voicePick.append(row);
      }
    };

    roleSelect.onchange = () => renderVoices();
    renderVoices();
    voiceField.append(voicePick, el('div', { className: 'voice-disclaimer' }, 'All voices are AI-generated.'));

    const actions = el('div', { className: 'add-form-actions' });
    const save = el('button', { className: 'btn', type: 'button' }, 'Add') as HTMLButtonElement;
    const cancel = el('button', { className: 'btn btn-ghost', type: 'button' }, 'Cancel') as HTMLButtonElement;
    cancel.onclick = () => {
      form.style.display = 'none';
    };
    save.onclick = async () => {
      const name = nameInput.value.trim().replace(/\s+/g, ' ');
      if (!name) return;
      if (!PERSON_NAME_RE.test(name) && !/^[A-Za-z][A-Za-z0-9 ]{0,11}$/.test(name)) {
        showToast('Use up to 12 letters or numbers (start with a letter)');
        return;
      }
      if (members.some((m) => m.name.toLowerCase() === name.toLowerCase())) {
        showToast('That name is already used');
        return;
      }
      if (members.length >= 10) {
        showToast('Up to 10 people in a Group');
        return;
      }
      const role = roleSelect.value === 'child' ? 'child' : 'adult';
      const voice = (voicePick.querySelector('input[name="voicePack"]:checked') as HTMLInputElement | null)
        ?.value ?? 'warm_mom';
      try {
        await createMember(name, role, voice);
        showToast('Member added');
        await renderFamily(container);
      } catch (e) {
        showToast(userMessage(e));
      }
    };
    actions.append(save, cancel);
    form.append(nameField, roleField, voiceField, actions);
    card.append(form);

    addChip.onclick = () => {
      form.style.display = form.style.display === 'none' ? 'block' : 'none';
      if (form.style.display === 'block') nameInput.focus();
    };

    container.append(card);
  } catch (e) {
    container.innerHTML = '';
    container.append(el('p', { className: 'list-empty' }, userMessage(e)));
  }
}
